<template>
  <q-drawer
    v-model="shown"
    side="left"
    behavior="mobile"
    overlay
    bordered
  >
    <q-list>
      <q-item
        v-if="$kds"
        v-ripple
        clickable
        @click="$emit('set-kitchen-display')"
      >
        <q-item-section
          avatar
        >
          <q-icon name="monitor" />
        </q-item-section>
        <q-item-section class="text-bold">
          Set Kitchen Display
        </q-item-section>
      </q-item>

      <q-separator />

      <q-item class="column">
        <div class="q-mx-md q-mt-sm">
          Order Limit
        </div>
        <q-btn-group
          outline
          spread
        >
          <q-btn
            v-for="limitOption in [10, 20, 30, 50]"
            :key="`orderLimitOption_${limitOption}`"
            :class="{ 'order-limit-selected': limit === limitOption }"
            :disable="limit === limitOption"
            :label="limitOption"
            @click="$emit('set-limit', limitOption)"
          />
        </q-btn-group>

        <q-separator />

        <div
          v-if="locations.length > 1"
          class="q-my-sm"
        >
          <span class="text-caption">Locations</span>
          <div
            class="row justify-center q-gutter-xs"
          >
            <q-chip
              label="All"
              square
              dense
              :outline="$_(locationsFilter).values().some()"
              clickable
              @click="$_.each(locationsFilter, (_, key) => { locationsFilter[key] = false })"
            />
            <q-chip
              v-for="location in locations"
              :key="location.id"
              :label="location.name"
              square
              dense
              :outline="!locationsFilter[location.id]"
              clickable
              @click="locationsFilter[location.id] = !locationsFilter[location.id]"
            />
          </div>
        </div>

        <div class="q-my-sm">
          <span class="text-caption">Delivery Types</span>
          <div class="row justify-center q-gutter-xs">
            <q-chip
              label="All"
              square
              dense
              :outline="$_(deliveryTypesFilter).values().some()"
              clickable
              @click="$_.each(deliveryTypesFilter, (_, key) => { deliveryTypesFilter[key] = false })"
            />
            <q-chip
              v-for="deliveryType in DELIVERY_TYPE_OPTIONS.KITCHEN"
              :key="deliveryType"
              :label="$_.startCase(deliveryType)"
              square
              dense
              :outline="!deliveryTypesFilter[deliveryType]"
              clickable
              @click="deliveryTypesFilter[deliveryType] = !deliveryTypesFilter[deliveryType]"
            />
          </div>
        </div>

        <div
          v-if="deliveryLocations.data.length > 0"
          class="q-my-sm"
        >
          <span class="text-caption">Delivery Locations</span>
          <div class="row justify-center q-gutter-xs">
            <q-chip
              label="All"
              square
              dense
              :outline="$_(deliveryLocationsFilter).values().some()"
              clickable
              @click="$_.each(deliveryLocationsFilter, (_, key) => { deliveryLocationsFilter[key] = false })"
            />
            <q-chip
              v-for="(deliveryLocation, index) in $_.map(deliveryLocations.data, 'name')"
              :key="`${deliveryLocation}-${index}`"
              :label="$_.startCase(deliveryLocation)"
              square
              dense
              :outline="!deliveryLocationsFilter[deliveryLocation]"
              clickable
              @click="deliveryLocationsFilter[deliveryLocation] = !deliveryLocationsFilter[deliveryLocation]"
            />
          </div>
        </div>
      </q-item>

      <q-separator />

      <q-select
        :model-value="prepareBy"
        class="q-mx-md q-mt-md"
        label="Prepare By"
        :options="$mh.asOptions(GROUP_BY.PREPARING)"
        map-options
        emit-value
        :rules="[val => val !== null || $t('form.errors.required')]"
        @update:model-value="value => $emit('set-prepare-by', value)"
      >
        <q-tooltip>
          The identifier to group preparation items against in a card.
        </q-tooltip>
      </q-select>

      <q-select
        :model-value="summarizeBy"
        class="q-mx-md"
        :label="$t('actions.summarizeBy')"
        :options="$mh.asOptions(GROUP_BY.SUMMARIZING)"
        map-options
        emit-value
        :rules="[val => val !== null || $t('form.errors.required')]"
        @update:model-value="value => $emit('set-summarize-by', value)"
      >
        <q-tooltip>
          The identifier to group summarized items against in summary views.
        </q-tooltip>
      </q-select>

      <q-separator />

      <q-item>
        <q-item-section
          class="column"
        >
          <div
            class="q-mx-md q-mt-sm"
          >
            Zoom
          </div>
          <q-slider
            :model-value="zoom"
            class="q-my-sm q-mx-auto"
            :label="true"
            :label-always="true"
            type="number"
            :min="0.5"
            :max="2"
            :step="0.1"
            @update:model-value="value => $emit('set-zoom', value)"
          />
        </q-item-section>
      </q-item>

      <q-separator />

      <q-item
        v-ripple
        class="q-mt-sm"
        clickable
        @click="reloadData"
      >
        <q-item-section
          avatar
        >
          <q-icon name="sync" />
        </q-item-section>
        <q-item-section class="text-bold">
          Reload Data
        </q-item-section>
      </q-item>

      <q-item
        v-ripple
        clickable
        @click="reloadApp"
      >
        <q-item-section
          avatar
        >
          <q-icon name="refresh" />
        </q-item-section>
        <q-item-section class="text-bold">
          Reload App & Data
        </q-item-section>
      </q-item>

      <q-item
        v-if="$versionManager.availableVersion"
        v-ripple
        clickable
        @click="$versionManager.updateSoftware(true)"
      >
        <q-item-section avatar>
          <q-icon
            :name="updateStyle.icon"
            :class="updateStyle.iconClass"
          />
        </q-item-section>
        <q-item-section>
          <div class="text-bold">
            {{ updateStyle.label }}
          </div>
          <div>{{ $versionManager.versionAvailableOnFrontendServer }}</div>
        </q-item-section>
      </q-item>

      <q-item
        class="text-grey-6 text-caption text-center"
      >
        <q-item-section>
          <div
            v-if="!currentCircuit.demoMode"
          >
            {{ currentSite.name }}
          </div>
          <div>
            Client Version {{ $versionManager.currentVersion }}
          </div>
          <div v-if="!currentCircuit.demoMode && currentUser && currentUser.superUser">
            Circuit: {{ currentCircuit.id }} - Site: {{ currentSite.id }}
            <br>
            Region: {{ region }} - Locale: {{ $i18n.locale }}
          </div>
        </q-item-section>
      </q-item>
    </q-list>
  </q-drawer>
</template>

<script>
import { region } from 'src/config'
import { DELIVERY_TYPE_OPTIONS } from 'src/models/delivery-type/DeliveryType'
import { GROUP_BY } from 'src/models/kitchen-display/KitchenDisplayType'

export default {
  name: 'KitchenSettingsDrawer',
  context: ['currentUser'],
  configuration: ['currentCircuit', 'currentSite', 'deliveryLocations'],
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    limit: {
      type: Number,
      required: true
    },
    locationsFilter: {
      type: Object,
      required: false,
      default: () => ({})
    },
    locations: {
      type: Array,
      required: false,
      default: () => ([])
    },
    deliveryTypesFilter: {
      type: Object,
      required: false,
      default: () => ({})
    },
    deliveryLocationsFilter: {
      type: Object,
      required: false,
      default: () => ({})
    },
    prepareBy: {
      type: String,
      required: false,
      default: GROUP_BY.NONE
    },
    summarizeBy: {
      type: String,
      required: false,
      default: GROUP_BY.VARIATION
    },
    zoom: {
      type: Number,
      required: true
    }
  },
  emits: ['set-kitchen-display', 'set-limit', 'set-prepare-by', 'set-summarize-by', 'set-zoom', 'update:model-value'],
  data () {
    return {
      shown: false
    }
  },
  computed: {
    updateStyle () {
      const style = {
        label: 'Update Software',
        icon: 'upgrade',
        iconClass: 'text-white bg-positive'
      }

      if (this.$versionManager.availableVersion === 'urgent') {
        style.iconClass = 'text-white bg-negative'
      }

      return style
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
    },
    shown () {
      this.$emit('update:model-value', this.shown)
    }
  },
  created () {
    _.extend(this, { DELIVERY_TYPE_OPTIONS, GROUP_BY, region })
  },
  methods: {
    async reloadData () {
      await this.configuration.validate()
      await this.context.fetch(true)
    },
    reloadApp () {
      window.location.reload()
    }
  }
}
</script>

<style scoped>
.order-limit-selected {
  background-color: white;
  color: var(--q-primary);
}
</style>
